import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { isFunction } from "@util/utils";
import useCart, { ADD_POSITION, UPDATE_POSITION } from "@util/hook/useCart";
import { FEEDBACK_TYPES } from "@util/hook/useCart";

/** 穩定參考，避免未傳 getProductKey 時依賴每 render 變動造成無限迴圈 */
const defaultGetProductKey = (product) => `${product.productID ?? ''}_${product.variantID ?? ''}`;

/**
 * 建立 SkuAddToList 場景適配 Hook 的工廠函數
 *
 * 作為 useCart 的 adapter：將 cart API 轉成 SkuAddToList 需要的 props，
 * 不同場景對應不同 quantityField 和 defaultQuantity
 *
 * @param {Object} config - 場景配置
 * @param {string} config.quantityField - 數量欄位名稱
 * @param {number} config.defaultQuantity - 加入商品時的預設數量
 * @param {string} [config.addPosition] - 新商品插入位置：ADD_POSITION.PREPEND（前）或 ADD_POSITION.APPEND（後），預設 PREPEND
 * @param {string} [config.addExistingPosition] - 重複加入同一商品時的位置：UPDATE_POSITION.PREPEND（前）、APPEND（後）或 KEEP（不動），預設 KEEP
 * @param {string} [config.updatePosition] - 更新商品時的位置：UPDATE_POSITION.PREPEND（前）、APPEND（後）或 KEEP（不動），預設 KEEP
 * @param {boolean} [config.removeWhenZero] - 數量更新為 ≤0 時是否移除商品，預設 true；盤點等情境可設 false 保留數量 0
 * @returns {Function} useSkuAddToList 場景 Hook
 */
export function createSkuAddToListHook({
	quantityField,
	defaultQuantity,
	addPosition = ADD_POSITION.PREPEND,
	addExistingPosition = UPDATE_POSITION.PREPEND,
	updatePosition = UPDATE_POSITION.KEEP,
	removeWhenZero = true,
}) {
	return function useSkuAddToList(options = {}) {
		const {
			fetchProductBySku: fetchProductBySkuProp,
			getFetchContext,
			getProductKey = defaultGetProductKey,
			initialProducts,
		} = options;

		const cart = useCart({ addPosition, addExistingPosition, updatePosition, removeWhenZero });
		const { cartItems, addToCart, updateQuantity, removeCartItem, clearCart, setCartItems, feedback, clearFeedback } = cart;

		const [isAdding, setIsAdding] = useState(false);
		const inFlightSkuRef = useRef(null);
		const pendingSkuRef = useRef(null);

		// 將 cartItems 轉成帶 quantityField 的 products（供 List、form submit 使用）
		const products = useMemo(
			() =>
				cartItems.map((item) => {
					const qty = item.quantity ?? item[quantityField];
					const numQty = qty !== undefined && qty !== null ? Number(qty) : defaultQuantity;
					const { quantity, ...rest } = item;
					return { ...rest, quantity: numQty, [quantityField]: numQty };
				}),
			// eslint-disable-next-line
			[cartItems, quantityField, defaultQuantity]
		);

		// 清空清單
		const clearProducts = clearCart;

		// initialProducts 同步到清單
		useEffect(() => {
			if (initialProducts === undefined) return;
			if (!Array.isArray(initialProducts) || initialProducts.length === 0) {
				setCartItems([]);
				return;
			}
			const items = initialProducts.map((p) => {
				const qty = p.quantity ?? p[quantityField];
				const id = getProductKey(p);
				return { ...p, id, quantity: qty !== undefined && qty !== null ? Number(qty) : defaultQuantity };
			});
			setCartItems(items);
		},
			// eslint-disable-next-line
			[initialProducts, setCartItems, quantityField, defaultQuantity, getProductKey]);

        // 依 SKU 取得商品
		const fetchProductBySku = useCallback(
			async (sku, context) => {
				if (typeof fetchProductBySkuProp !== "function") return null;
				const fetchContext = getFetchContext ? getFetchContext() : context;
				const product = await fetchProductBySkuProp(sku, fetchContext);
				if (!product) return null;
				return { ...product, quantity: defaultQuantity };
			},
			// eslint-disable-next-line
			[fetchProductBySkuProp, getFetchContext, defaultQuantity]
		);

        // 從商品取得數量
		const getQuantityFromProduct = useCallback(
			(product) => {
				const qty = product?.quantity ?? product?.[quantityField];
				return qty !== undefined && qty !== null ? Number(qty) : defaultQuantity;
			},
			// eslint-disable-next-line
			[quantityField, defaultQuantity]
		);

		// 加入商品
		const handleAddProduct = useCallback(
			async (sku) => {
				const trimmedSku = sku?.trim();
				if (!trimmedSku || isAdding) return false;

				if (inFlightSkuRef.current === trimmedSku) {
					pendingSkuRef.current = trimmedSku;
					return false;
				}

				setIsAdding(true);
				inFlightSkuRef.current = trimmedSku;

				try {
					if (!isFunction(fetchProductBySkuProp)) {
						throw new Error("fetchProductBySku 函數未提供");
					}

					const newProduct = await fetchProductBySku(trimmedSku);
					if (!newProduct) return false;

					const id = getProductKey(newProduct);
					const initialQty =
						typeof defaultQuantity === "number" ? defaultQuantity : 1;
					const item = {
						...newProduct,
						id,
						quantity: initialQty,
						[quantityField]: initialQty,
						name: newProduct.name ?? newProduct.productName,
						currentPrice: newProduct.currentPrice ?? 0,
					};
					addToCart(item, initialQty);
					return true;
				} catch (err) {
					console.error("新增商品失敗:", err);
					return false;
				} finally {
					setIsAdding(false);
					inFlightSkuRef.current = null;
				}
			},
			// eslint-disable-next-line
			[isAdding, fetchProductBySku, addToCart, getProductKey, quantityField, defaultQuantity]
		);

		// 防重複請求 + 不丟操作
		useEffect(() => {
			if (!isAdding && pendingSkuRef.current) {
				const sku = pendingSkuRef.current;
				pendingSkuRef.current = null;
				handleAddProduct(sku);
			}
		},
			// eslint-disable-next-line
			[isAdding, handleAddProduct]);

        // 修改數量
		const handleQuantityChange = useCallback(
			(productKey, quantity) => {
				updateQuantity(productKey, Number(quantity));
			},
			// eslint-disable-next-line
			[updateQuantity]
		);

        // 刪除商品
		const handleDeleteProduct = useCallback(
			(productKey) => {
				removeCartItem(productKey);
			},
			// eslint-disable-next-line
			[removeCartItem]
		);

		/**
		 * manualEditTarget：使用者要編輯/加入(一筆商品)的資訊。
		 * { productKey: string, product: object, source: "edit" }；null 表示「沒有從清單點擊選中的那一筆」（彈窗可能關閉，或是由剛加入的 feedback 開啟）。
		 */
		const [manualEditTarget, setManualEditTarget] = useState(null);

		// feedback 轉成 quantityEditTarget（加入車：有倒數）；manualEditTarget 優先
		const quantityEditTarget = useMemo(() => {
			if (manualEditTarget) return { ...manualEditTarget, openId: manualEditTarget.productKey };
			if (feedback?.type === FEEDBACK_TYPES.ADD_SUCCESS && feedback?.item) {
				const item = feedback.item;
				return {
					productKey: item.id,
					product: item,
					source: "add",
					openId: feedback.timestamp ?? `${item.id}_${Date.now()}`,
				};
			}
			return null;
		},
			// eslint-disable-next-line
			[feedback, manualEditTarget]);

		// 確認數量並關閉彈窗
		const handleConfirmQuantity = useCallback(
			(productKey, quantity) => {
				updateQuantity(productKey, Number(quantity));
				setManualEditTarget(null);
				clearFeedback();
			},
			// eslint-disable-next-line
			[updateQuantity, clearFeedback]
		);

		// 僅關閉彈窗
		const handleCloseQuantityEdit = useCallback(() => {
			setManualEditTarget(null);
			clearFeedback();
		},
			// eslint-disable-next-line
			[clearFeedback]);

		// 從清單點擊數量開啟編輯彈窗（設定 manualEditTarget 即有 quantityEditTarget，彈窗由 !!quantityEditTarget 決定開關）
		const handleOpenQuantityEdit = useCallback(
			(product) => {
				if (!product) return;
				const key = typeof getProductKey === "function" ? getProductKey(product) : `${product.productID ?? ''}_${product.variantID ?? ''}`;
				setManualEditTarget({ productKey: key, product, source: "edit" });
				clearFeedback();
			},
			// eslint-disable-next-line
			[getProductKey, clearFeedback]
		);

		return {
			products,
			clearProducts,
			fetchProductBySku,
			getProductKey,
			getQuantityFromProduct,
			defaultQuantity,
			handleAddProduct,
			handleQuantityChange,
			handleDeleteProduct,
			quantityEditTarget,
			handleOpenQuantityEdit,
			handleConfirmQuantity,
			handleCloseQuantityEdit,
			isAdding,
		};
	};
}

/** 進貨單場景：expectedQuantity，預設 1；SkuAddConfirmDialog 確認時移到最前面 */
export const usePurchaseSkuAdd = createSkuAddToListHook({
	quantityField: "expectedQuantity",
	defaultQuantity: 1,
	updatePosition: UPDATE_POSITION.PREPEND,
});

/** 進貨驗收場景：actualQuantity，預設 1；SkuAddConfirmDialog 確認時移到最前面；驗收數量填 0 仍保留列 */
export const usePurchaseInspectionSkuAdd = createSkuAddToListHook({
	quantityField: "actualQuantity",
	defaultQuantity: 1,
	updatePosition: UPDATE_POSITION.PREPEND,
	removeWhenZero: false,
});

/** 盤點場景：countedQuantity，預設 0；數量改為 0 時保留在清單不移除 */
export const useInventorySkuAdd = createSkuAddToListHook({
	quantityField: "countedQuantity",
	defaultQuantity: 0,
	updatePosition: UPDATE_POSITION.PREPEND,
	removeWhenZero: false,
});

/** 領用場景：claimQuantity，預設 1；SkuAddConfirmDialog 確認時移到最前面 */
export const useClaimSkuAdd = createSkuAddToListHook({
	quantityField: "claimQuantity",
	defaultQuantity: 1,
	updatePosition: UPDATE_POSITION.PREPEND,
});

/** 調撥場景：quantity，預設 1；SkuAddConfirmDialog 確認時移到最前面 */
export const useTransferSkuAdd = createSkuAddToListHook({
	quantityField: "quantity",
	defaultQuantity: 1,
	updatePosition: UPDATE_POSITION.PREPEND,
});

export { ADD_POSITION, UPDATE_POSITION };
